import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from '../config';
import { useAuth } from '../auth';
import { IconPhone, IconSignal, IconBuilding } from '../components/Icons';
import VodafoneBadge from '../components/VodafoneBadge';

const nf = (n) => (n || 0).toLocaleString('it-IT');
const R = 31, CIRC = 2 * Math.PI * R;
const COL_COP = '#3b82f6', COL_SSM = '#eab308', COL_VF = '#e60000', COL_IND = '#8b5cf6';

// icona Indaga: lente (coerente con le altre, niente emoji)
const IconIndaga = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
);

function Ring({ value, label, sub, frac, color }) {
  const off = CIRC * (1 - Math.max(0.05, Math.min(1, frac || 0)));
  return (
    <div className="m-ring">
      <svg width="74" height="74" viewBox="0 0 74 74">
        <circle className="trk" cx="37" cy="37" r={R} />
        <circle cx="37" cy="37" r={R} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={CIRC} strokeDashoffset={off} transform="rotate(-90 37 37)" />
        <text x="37" y="42" textAnchor="middle" className="rv">{value}</text>
      </svg>
      <div className="rl">{label}</div><div className="rs">{sub}</div>
    </div>
  );
}

function Gauge({ value, sub, frac, color }) {
  const f = Math.max(0, Math.min(1, frac || 0));
  const ex = 95 + 80 * Math.cos(Math.PI * (1 - f));
  const ey = 100 - 80 * Math.sin(Math.PI * (1 - f));
  return (
    <svg width="190" height="116" viewBox="0 0 190 116">
      <path d="M15 100 A 80 80 0 0 1 175 100" fill="none" className="trk" strokeWidth="12" strokeLinecap="round" />
      <path d={`M15 100 A 80 80 0 0 1 ${ex.toFixed(1)} ${ey.toFixed(1)}`} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" />
      <text x="95" y="88" textAnchor="middle" className="gv">{value}</text>
      <text x="95" y="106" textAnchor="middle" className="gs">{sub}</text>
    </svg>
  );
}

// Slider "scorri per confermare" (stile iPhone slide-to-power-off): si trascina il pallino
// fino in fondo per eseguire l'azione. Rilasciando prima della soglia, torna indietro fluido.
function SlideToggle({ on, disabled, label, onCommit }) {
  const trackRef = useRef(null);
  const st = useRef({ startX: 0, max: 0, active: false });
  const xRef = useRef(0);
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const KNOB = 40, PAD = 4;

  const down = (e) => {
    if (disabled) return;
    const w = trackRef.current ? trackRef.current.clientWidth : 0;
    st.current = { startX: e.clientX, max: Math.max(0, w - KNOB - PAD * 2), active: true };
    setDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const move = (e) => {
    if (!st.current.active) return;
    const nx = Math.max(0, Math.min(st.current.max, e.clientX - st.current.startX));
    xRef.current = nx; setX(nx);
  };
  const up = () => {
    if (!st.current.active) return;
    st.current.active = false;
    setDragging(false);
    const committed = st.current.max > 0 && xRef.current >= st.current.max * 0.85;
    xRef.current = 0; setX(0);
    if (committed) onCommit();
  };

  return (
    <div ref={trackRef} className={`m-slide ${on ? 'on' : ''} ${disabled ? 'dis' : ''}`}>
      <span className="lbl">{label}</span>
      <span className="kn" style={{ transform: `translateX(${x}px)`, transition: dragging ? 'none' : 'transform .28s cubic-bezier(.22,1,.36,1)' }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
      </span>
    </div>
  );
}

// legenda spicchi del tachimetro per ciascun agente: [etichetta, chiave-spicchio, classe-pallino]
const GAUGE_LEG = {
  '456': [['NON TIM', 'non_tim', 'dn'], ['TIM', 'tim', 'dt'], ['Err', 'err', 'de']],
  copertura: [['Coperto', 'coperto', 'dt'], ['Non cop.', 'non_coperto', 'de']],
  ssmate: [['Con lucchetto', 'con_lucchetto', 'dt'], ['Senza', 'senza', 'dn']],
  vf: [['Vodafone', 'vodafone', 'de'], ['Altri', 'non_vodafone', 'dn']],
  indaga: [['Completati', 'completati', 'dt'], ['Non conf.', 'non_confermati', 'dn'], ['Non trov.', 'non_trovati', 'de']],
};

export default function StatoPage() {
  const [ag, setAg] = useState(null);
  const [cop, setCop] = useState(null);
  const [ssm, setSsm] = useState(null);
  const [stats, setStats] = useState(null);
  const [copDash, setCopDash] = useState(null);
  const [ssDash, setSsDash] = useState(null);
  const [lead, setLead] = useState(0);
  const [newFtth, setNewFtth] = useState(0);
  const [gea, setGea] = useState(null);
  const [aind, setAind] = useState(null);
  const [a24, setA24] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { can, user } = useAuth();
  const show456 = can('verifiche');
  const showCop = can('agente_copertura');
  const showSsmate = can('ssmate');
  const showGea = can('gea');
  const showIndaga = user?.ruolo === 'admin';
  const showKpi = can('dashboard');

  const loadLive = useCallback(() => {
    axios.get(`${API}/agent/status`).then((r) => setAg(r.data)).catch(() => {});
    axios.get(`${API}/copertura/stato`).then((r) => setCop(r.data)).catch(() => {});
    if (showSsmate) axios.get(`${API}/ssmate/bulk/preview`).then((r) => setSsm(r.data)).catch(() => {});
    if (showGea) axios.get(`${API}/gea/stato`).then((r) => setGea(r.data)).catch(() => {});
    if (showIndaga) axios.get(`${API}/indaga/stato`).then((r) => setAind(r.data)).catch(() => {});
  }, [showSsmate, showGea, showIndaga]);
  const loadKpi = useCallback(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/copertura/dashboard`).then((r) => setCopDash(r.data)).catch(() => {});
    axios.get(`${API}/numeri?lead=true&limit=1`).then((r) => setLead(r.data?.total ?? 0)).catch(() => {});
    if (showCop) axios.get(`${API}/numeri?copertura_new=true&limit=1`).then((r) => setNewFtth(r.data?.total ?? 0)).catch(() => {});
    if (showSsmate) axios.get(`${API}/ssmate/dashboard`).then((r) => setSsDash(r.data)).catch(() => {});
    axios.get(`${API}/stats/agenti-24h`).then((r) => setA24(r.data)).catch(() => {});
  }, [showCop, showSsmate]);

  useEffect(() => {
    loadLive(); loadKpi();
    const t1 = setInterval(loadLive, 3000);
    const t2 = setInterval(loadKpi, 20000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [loadLive, loadKpi]);

  // elenco agenti permessi (memoizzato: evita che l'effect di default-selezione giri a ogni render)
  const agents = React.useMemo(() => [
    show456 && { key: '456', label: 'AI 456', icon: <IconPhone size={18} />, color: 'var(--signal)', on: !!ag?.attivo, toggle: '/agent/toggle', attivo: !!ag?.attivo, abilitata: true },
    showCop && { key: 'copertura', label: 'AI Copertura', icon: <IconSignal size={18} />, color: COL_COP, on: !!cop?.agente_attivo, toggle: '/copertura/agente', attivo: !!cop?.agente_attivo, abilitata: cop?.abilitata !== false },
    showSsmate && { key: 'ssmate', label: 'AI SSMATE', icon: <IconBuilding size={18} />, color: COL_SSM, on: !!ssm?.agente_attivo, toggle: '/ssmate/agente', attivo: !!ssm?.agente_attivo, abilitata: true },
    showGea && { key: 'vf', label: 'AI VF', icon: <VodafoneBadge size={18} />, color: COL_VF, on: !!gea?.agente_attivo, toggle: '/gea/agente', attivo: !!gea?.agente_attivo, abilitata: gea?.abilitata !== false },
    showIndaga && { key: 'indaga', label: 'AI Indaga', icon: <IconIndaga size={18} />, color: COL_IND, on: !!aind?.agente_attivo, toggle: '/indaga/agente', attivo: !!aind?.agente_attivo, abilitata: aind?.abilitata !== false },
  ].filter(Boolean), [show456, showCop, showSsmate, showGea, showIndaga,
    ag?.attivo, cop?.agente_attivo, cop?.abilitata, ssm?.agente_attivo, gea?.agente_attivo, gea?.abilitata, aind?.agente_attivo, aind?.abilitata]);

  // default: 456 se permesso, altrimenti il primo disponibile
  useEffect(() => {
    if (sel && agents.some((a) => a.key === sel)) return;
    if (agents.length) setSel(agents[0].key);
  }, [agents, sel]);

  const cur = agents.find((a) => a.key === sel) || null;

  // KPI derivati
  const ftth = copDash?.per_tecnologia?.FTTH || 0;
  const coperti = copDash?.per_esito?.COPERTO || 0;
  const ftthPct = coperti ? Math.round((100 * ftth) / coperti) : 0;
  const lavorabili = ssDash?.verifiche?.lavorabili || 0;
  const nonTim = stats?.non_tim || 0;

  const ringFor = (k) => {
    if (k === '456') return <Ring value={nf(lead)} label="Lead" sub="NON TIM · fibra" color="var(--signal)" frac={nonTim ? lead / nonTim : 0.3} />;
    if (k === 'copertura') return <Ring value={`${ftthPct}%`} label="Coperti FTTH" sub="sui coperti" color={COL_COP} frac={ftthPct / 100} />;
    if (k === 'ssmate') return <Ring value={nf(lavorabili)} label="Presidio" sub="lavorabili" color={COL_SSM} frac={0.4} />;
    if (k === 'vf') return <Ring value={nf(gea?.vodafone)} label="Vodafone" sub={`${nf(gea?.eligibili)} da fare`} color={COL_VF} frac={0.4} />;
    if (k === 'indaga') return <Ring value={nf(aind?.eligibili)} label="Completabili" sub={`tetto ${nf(aind?.tetto)}/g`} color={COL_IND} frac={0.4} />;
    return null;
  };

  const a24key = (k) => (k === '456' ? 'a456' : k);
  const a24cur = cur ? (a24?.[a24key(cur.key)] || null) : null;
  const gaugeFrac = (() => {
    if (!a24cur || !a24cur.tot) return 0;
    const first = GAUGE_LEG[cur.key]?.[0]?.[1];
    return first ? (a24cur.spicchi?.[first] || 0) / a24cur.tot : 0;
  })();

  const performToggle = async () => {
    if (!cur || busy || cur.abilitata === false) return;
    setBusy(true);
    try { await axios.post(`${API}${cur.toggle}`, { attivo: !cur.attivo }); loadLive(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setBusy(false);
  };

  if (!agents.length && !showKpi) {
    return <div className="m-empty">Nessuna sezione abilitata per il tuo utente.</div>;
  }

  return (
    <>
      {agents.length > 0 && (
        <div className="m-agentbar">
          {agents.map((a) => (
            <button key={a.key} type="button" className={`m-agicon ${sel === a.key ? 'on' : ''}`}
              style={sel === a.key ? { '--ac': a.color } : undefined}
              onClick={() => setSel(a.key)} aria-label={a.label}>
              {a.icon}
              <span className={`st ${a.on ? 'on' : ''}`} />
            </button>
          ))}
        </div>
      )}

      {showCop && newFtth > 0 && (
        <button className="m-alert2" onClick={() => nav('/liste?ftth=1')}>
          <span className="ab"><IconSignal size={13} /></span>
          <span className="at"><b>{nf(newFtth)}</b> {newFtth > 1 ? 'clienti passati' : 'cliente passato'} a <b>FTTH</b> — contattali per primo</span>
          <span className="aa">→</span>
        </button>
      )}

      {cur && (
        <>
          {showKpi && <div className="m-ringcard">{ringFor(cur.key)}</div>}

          <div className="m-gcard">
            <div className="m-gh"><span className="gd" /> {cur.label} · 24h
              <span className="gu">{cur.on ? 'attivo' : 'fermo'}</span>
            </div>
            <div className="m-gwrap">
              <Gauge value={nf(a24cur?.tot)} sub="ultime 24h" frac={gaugeFrac} color={cur.color} />
              <div className="m-glabels">
                {(GAUGE_LEG[cur.key] || []).map(([lab, key, cls]) => (
                  <span key={key}><i className={cls} />{lab} {a24cur?.tot ? Math.round((100 * (a24cur.spicchi?.[key] || 0)) / a24cur.tot) : 0}%</span>
                ))}
              </div>
            </div>
            <SlideToggle on={cur.on} disabled={busy || cur.abilitata === false}
              label={cur.abilitata === false ? "Disabilitato dall'admin" : (cur.on ? 'Scorri per fermare' : 'Scorri per avviare')}
              onCommit={performToggle} />
          </div>
        </>
      )}
    </>
  );
}
